// src/features/dashboard/pages/RMDashboard.tsx

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Navigate,
} from 'react-router-dom';

import DashboardHeader from '../components/DashboardHeader';

import KPISection from '../components/KPISection';

import ChartsSection from '../components/ChartsSection';

import AppList from '../components/AppList';

import Heatmap from '../components/Heatmap';

import {
  getSpanOfControlHeader,
} from '../services/spanOfControl.service';

import type {
  SpanOfControlHeaderResponse,
} from '../types/spanOfControl.types';

import {
  ManagerDateProvider,
  useManagerDate,
  formatManagerDate,
} from '../context/ManagerDateContext';

import {
  getManagerEmployeeCode,
} from '../../../features/auth/utils/Login/authStorage';

import {
  useLoaderContext,
} from '../../../components/Loader/LoaderContext';


/* ============================================================================
 * BROWSER ZOOM AUTO REFRESH
 * ========================================================================== */

/**
 * Chrome does not expose a universal "page zoom changed" event.
 *
 * This hook uses:
 *   - Ctrl/Cmd + / - / 0 keyboard shortcuts
 *   - devicePixelRatio changes
 *   - CSS resolution media-query changes
 *   - visualViewport changes
 *   - resize events
 *   - polling fallback
 *
 * IMPORTANT:
 * We do NOT reload on every resize by itself.
 * A resize is only treated as a zoom change when the zoom indicators
 * change as well. This avoids refreshing for an ordinary window resize.
 *
 * After a zoom change is detected, the page reloads once.
 */

const useBrowserZoomAutoRefresh = () => {
  const mountedRef = useRef(true);
  const reloadStartedRef = useRef(false);

  const initialDprRef = useRef(
    Number(
      window.devicePixelRatio.toFixed(3),
    ),
  );

  const initialInnerWidthRef = useRef(
    window.innerWidth,
  );

  const initialOuterWidthRef = useRef(
    window.outerWidth,
  );

  const initialScreenWidthRef = useRef(
    window.screen.width,
  );

  const resizeTimerRef =
    useRef<number | null>(null);

  const pollTimerRef =
    useRef<number | null>(null);

  const reloadTimerRef =
    useRef<number | null>(null);

  const resolutionQueryRef =
    useRef<MediaQueryList | null>(null);

  const resolutionHandlerRef =
    useRef<(() => void) | null>(null);

  const getZoomSignature = useCallback(() => {
    const dpr =
      Number(
        window.devicePixelRatio.toFixed(3),
      );

    const innerWidth =
      window.innerWidth;

    const outerWidth =
      window.outerWidth;

    const screenWidth =
      window.screen.width;

    /*
     * At desktop Chrome page zoom, CSS viewport width normally changes
     * relative to the browser window. This ratio is used only as a
     * secondary signal.
     */
    const viewportRatio =
      innerWidth > 0
        ? outerWidth / innerWidth
        : 1;

    /*
     * Another useful signal is screen CSS width relative to viewport CSS
     * width. It changes when browser page zoom changes.
     */
    const screenRatio =
      innerWidth > 0
        ? screenWidth / innerWidth
        : 1;

    return {
      dpr,
      innerWidth,
      outerWidth,
      screenWidth,
      viewportRatio,
      screenRatio,
    };
  }, []);

  const reloadPage = useCallback(
    (reason: string) => {
      if (
        reloadStartedRef.current ||
        !mountedRef.current
      ) {
        return;
      }

      reloadStartedRef.current = true;

      console.log(
        '[WorkVista] Browser zoom changed. Reloading page.',
        {
          reason,
          ...getZoomSignature(),
        },
      );

      reloadTimerRef.current =
        window.setTimeout(() => {
          if (!mountedRef.current) {
            return;
          }

          /*
           * location.reload() forces the complete page/application
           * reload instead of only React re-rendering.
           */
          window.location.reload();
        }, 150);
    },
    [getZoomSignature],
  );

  useEffect(() => {
    mountedRef.current = true;
    reloadStartedRef.current = false;

    /*
     * Capture the browser state AFTER the component mounts.
     */
    const initial =
      getZoomSignature();

    initialDprRef.current =
      initial.dpr;

    initialInnerWidthRef.current =
      initial.innerWidth;

    initialOuterWidthRef.current =
      initial.outerWidth;

    initialScreenWidthRef.current =
      initial.screenWidth;

    // ========================================================================
    // CHECK ZOOM
    // ========================================================================

    const checkZoom = (
      allowViewportFallback = false,
    ) => {
      if (reloadStartedRef.current) {
        return;
      }

      const current =
        getZoomSignature();

      /*
       * Signal 1:
       * devicePixelRatio.
       */
      const dprChanged =
        Math.abs(
          current.dpr -
            initialDprRef.current,
        ) >= 0.01;

      if (dprChanged) {
        reloadPage(
          'devicePixelRatio changed',
        );
        return;
      }

      /*
       * Signal 2:
       * outerWidth / innerWidth ratio.
       */
      const initialViewportRatio =
        initialInnerWidthRef.current > 0
          ? initialOuterWidthRef.current /
            initialInnerWidthRef.current
          : 1;

      const viewportRatioChanged =
        Math.abs(
          current.viewportRatio -
            initialViewportRatio,
        ) >= 0.025;

      if (viewportRatioChanged) {
        reloadPage(
          'Chrome viewport zoom ratio changed',
        );
        return;
      }

      /*
       * Signal 3:
       * screen.width / innerWidth ratio.
       */
      const initialScreenRatio =
        initialInnerWidthRef.current > 0
          ? initialScreenWidthRef.current /
            initialInnerWidthRef.current
          : 1;

      const screenRatioChanged =
        Math.abs(
          current.screenRatio -
            initialScreenRatio,
        ) >= 0.025;

      if (screenRatioChanged) {
        reloadPage(
          'screen-to-viewport zoom ratio changed',
        );
        return;
      }

      /*
       * Optional final fallback.
       *
       * We only use this after a resize/visualViewport event.
       * This catches Chrome configurations where the zoom changes the
       * viewport but does not update the other signals.
       *
       * It is deliberately disabled for the normal polling loop so that
       * ordinary browser window resizing does not cause endless refreshes.
       */
      if (allowViewportFallback) {
        const widthChanged =
          current.innerWidth !==
          initialInnerWidthRef.current;

        /*
         * Chrome page zoom can change innerWidth substantially.
         * Use a large enough threshold to avoid treating tiny layout
         * changes as a zoom.
         */
        const widthDifference =
          Math.abs(
            current.innerWidth -
              initialInnerWidthRef.current,
          );

        const significantWidthChange =
          widthChanged &&
          widthDifference >= 80;

        if (significantWidthChange) {
          reloadPage(
            'significant Chrome viewport width change',
          );
        }
      }
    };

    // ========================================================================
    // WINDOW RESIZE
    // ========================================================================

    const handleResize = () => {
      if (
        resizeTimerRef.current !== null
      ) {
        window.clearTimeout(
          resizeTimerRef.current,
        );
      }

      resizeTimerRef.current =
        window.setTimeout(() => {
          checkZoom(true);
        }, 200);
    };

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
      },
    );

    // ========================================================================
    // VISUAL VIEWPORT
    // ========================================================================

    const visualViewport =
      window.visualViewport;

    const handleVisualViewportResize =
      () => {
        window.setTimeout(() => {
          checkZoom(true);
        }, 100);
      };

    if (visualViewport) {
      visualViewport.addEventListener(
        'resize',
        handleVisualViewportResize,
      );
    }

    // ========================================================================
    // KEYBOARD ZOOM SHORTCUTS
    // ========================================================================

    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      const modifierPressed =
        event.ctrlKey ||
        event.metaKey;

      if (!modifierPressed) {
        return;
      }

      /*
       * Chrome:
       *
       * Ctrl + +
       * Ctrl + =
       * Ctrl + -
       * Ctrl + _
       * Ctrl + 0
       *
       * Also supports Cmd on macOS.
       */
      const isZoomShortcut =
        event.key === '+' ||
        event.key === '=' ||
        event.key === '-' ||
        event.key === '_' ||
        event.key === '0';

      if (!isZoomShortcut) {
        return;
      }

      /*
       * Do not immediately reload.
       * Chrome needs a moment to apply the new zoom level.
       */
      window.setTimeout(() => {
        checkZoom(true);

        /*
         * If Chrome's zoom implementation does not expose any of the
         * standard signals, the keyboard shortcut itself is definitive.
         *
         * This fallback therefore reloads after Ctrl+/Cmd+ zoom.
         */
        if (
          !reloadStartedRef.current
        ) {
          reloadPage(
            `browser zoom keyboard shortcut: ${event.key}`,
          );
        }
      }, 300);
    };

    window.addEventListener(
      'keydown',
      handleKeyDown,
    );

    // ========================================================================
    // CSS RESOLUTION MEDIA QUERY
    // ========================================================================

    const setupResolutionQuery =
      () => {
        const dpr =
          Number(
            window.devicePixelRatio.toFixed(3),
          );

        const query =
          window.matchMedia(
            `(resolution: ${dpr}dppx)`,
          );

        const handleResolutionChange =
          () => {
            window.setTimeout(() => {
              checkZoom(true);
            }, 100);
          };

        resolutionQueryRef.current =
          query;

        resolutionHandlerRef.current =
          handleResolutionChange;

        if (
          typeof query.addEventListener ===
          'function'
        ) {
          query.addEventListener(
            'change',
            handleResolutionChange,
          );
        } else {
          query.addListener(
            handleResolutionChange,
          );
        }
      };

    setupResolutionQuery();

    // ========================================================================
    // POLLING FALLBACK
    // ========================================================================

    pollTimerRef.current =
      window.setInterval(() => {
        checkZoom(false);
      }, 100);

    // Initial check.
    checkZoom(false);

    // ========================================================================
    // CLEANUP
    // ========================================================================

    return () => {
      mountedRef.current = false;

      window.removeEventListener(
        'resize',
        handleResize,
      );

      window.removeEventListener(
        'keydown',
        handleKeyDown,
      );

      if (visualViewport) {
        visualViewport.removeEventListener(
          'resize',
          handleVisualViewportResize,
        );
      }

      if (
        resizeTimerRef.current !== null
      ) {
        window.clearTimeout(
          resizeTimerRef.current,
        );

        resizeTimerRef.current = null;
      }

      if (
        pollTimerRef.current !== null
      ) {
        window.clearInterval(
          pollTimerRef.current,
        );

        pollTimerRef.current = null;
      }

      if (
        reloadTimerRef.current !== null
      ) {
        window.clearTimeout(
          reloadTimerRef.current,
        );

        reloadTimerRef.current = null;
      }

      const query =
        resolutionQueryRef.current;

      const handler =
        resolutionHandlerRef.current;

      if (query && handler) {
        if (
          typeof query.removeEventListener ===
          'function'
        ) {
          query.removeEventListener(
            'change',
            handler,
          );
        } else {
          query.removeListener(handler);
        }
      }

      resolutionQueryRef.current =
        null;

      resolutionHandlerRef.current =
        null;
    };
  }, [
    getZoomSignature,
    reloadPage,
  ]);
};


/* ============================================================================
 * SMALL DATE CHANGE LOADER
 * ========================================================================== */

const DateChangeLoader = () => {

  return (
    <div
      className="
        absolute
        inset-0
        z-[100]
        flex
        items-start
        justify-center
        bg-white/45
        pt-[70px]
        backdrop-blur-[2px]
      "
    >

      <div
        className="
          flex
          h-[92px]
          w-[158px]
          flex-col
          items-center
          justify-center
          rounded-[12px]
          border
          border-[#E5E7EB]
          bg-white
          shadow-[0_4px_14px_rgba(0,0,0,0.14)]
        "
      >

        {/* Spinner */}

        <div
          className="
            h-[30px]
            w-[30px]
            animate-spin
            rounded-full
            border-[3px]
            border-[#E5E7EB]
            border-t-[#2563EB]
          "
        />

        {/* Main text */}

        <p
          className="
            mt-[7px]
            text-[12px]
            font-semibold
            leading-[16px]
            text-[#344054]
          "
        >
          Loading data...
        </p>

        {/* Secondary text */}

        <p
          className="
            mt-[2px]
            text-[10px]
            leading-[14px]
            text-[#98A2B3]
          "
        >
          Updating selected date
        </p>

      </div>

    </div>
  );
};


/* ============================================================================
 * DASHBOARD CONTENT
 * ========================================================================== */

const DashboardContent = () => {

  /* ==========================================================================
   * BROWSER ZOOM AUTO REFRESH
   * ========================================================================== */

  useBrowserZoomAutoRefresh();


  /* ==========================================================================
   * MANAGER EMPLOYEE CODE
   * ========================================================================== */

  const ManagerEmployeeCode =
    getManagerEmployeeCode();


  /* ==========================================================================
   * LOADER CONTEXT
   *
   * Global loader is used only for initial page load.
   * Date changes use the small dashboard loader.
   * ========================================================================== */

  const {
    showLoader,
    hideLoader,
  } = useLoaderContext();


  /* ==========================================================================
   * MANAGER DATE CONTEXT
   * ========================================================================== */

  const {
    fromDate,
    toDate,
  } = useManagerDate();


  /* ==========================================================================
   * WORK MODE
   * ========================================================================== */

  const [
    workMode,
    setWorkMode,
  ] = useState<string>('All');


  /* ==========================================================================
   * HEADER DATA
   * ========================================================================== */

  const [
    headerData,
    setHeaderData,
  ] = useState<SpanOfControlHeaderResponse | null>(
    null,
  );


  /* ==========================================================================
   * DATE CHANGE LOADING
   * ========================================================================== */

  const [
    dateLoading,
    setDateLoading,
  ] = useState<boolean>(false);


  /* ==========================================================================
   * FIRST RENDER
   * ========================================================================== */

  const isFirstRender =
    useRef<boolean>(true);


  /* ==========================================================================
   * DATE STRINGS
   *
   * IMPORTANT:
   *
   * DO NOT USE:
   *
   *   fromDate.toISOString().split('T')[0]
   *
   * Because the selected date is a LOCAL calendar date.
   *
   * Example in IST:
   *
   *   13-Sep-2026 00:00 IST
   *
   * toISOString() becomes:
   *
   *   12-Sep-2026 18:30 UTC
   *
   * Therefore split('T')[0] becomes:
   *
   *   2026-09-12
   *
   * which is WRONG.
   *
   * Use formatManagerDate() instead.
   *
   * 13-Sep-2026
   *      ↓
   * 2026-09-13
   * ========================================================================== */

  const fromDateString =
    formatManagerDate(
      fromDate,
    );

  const toDateString =
    formatManagerDate(
      toDate,
    );


  /* ==========================================================================
   * DEBUG SELECTED DATE
   * ========================================================================== */

  console.log(
    '==========================================',
  );

  console.log(
    'MANAGER DASHBOARD DATE',
  );

  console.log(
    'From Date Object:',
    fromDate,
  );

  console.log(
    'To Date Object:',
    toDate,
  );

  console.log(
    'From Date API:',
    fromDateString,
  );

  console.log(
    'To Date API:',
    toDateString,
  );

  console.log(
    '==========================================',
  );


  /* ==========================================================================
   * INITIAL + DATE CHANGE API
   *
   * INITIAL LOAD:
   *
   *   showLoader()
   *   API
   *   hideLoader()
   *
   * DATE CHANGE:
   *
   *   setDateLoading(true)
   *   API
   *   setDateLoading(false)
   * ========================================================================== */

  useEffect(() => {

    let mounted =
      true;

    const isInitialLoad =
      isFirstRender.current;


    /* ========================================================================
     * INITIAL LOAD
     * ======================================================================== */

    if (isInitialLoad) {

      isFirstRender.current =
        false;

      showLoader();

    } else {

      /* ======================================================================
       * DATE CHANGE
       * ====================================================================== */

      setDateLoading(
        true,
      );
    }


    /* ========================================================================
     * LOAD DASHBOARD
     * ======================================================================== */

    const loadDashboard =
      async () => {

        try {

          /* ==================================================================
           * MANAGER VALIDATION
           * ================================================================== */

          if (
            !ManagerEmployeeCode
          ) {

            console.warn(
              'Manager Employee Code is missing.',
            );

            return;
          }


          /* ==================================================================
           * HEADER API
           * ================================================================== */

          const response =
            await getSpanOfControlHeader(
              {
                ManagerEmployeeCode,
              },
            );


          /* ==================================================================
           * COMPONENT CHECK
           * ================================================================== */

          if (!mounted) {
            return;
          }


          /* ==================================================================
           * SAVE HEADER DATA
           * ================================================================== */

          setHeaderData(
            response,
          );

        } catch (
          error
        ) {

          console.error(
            'Reporting Manager Dashboard Error:',
            error,
          );


          if (mounted) {

            setHeaderData(
              null,
            );
          }

        } finally {

          /* ==================================================================
           * INITIAL LOAD
           * ================================================================== */

          if (isInitialLoad) {

            hideLoader();

          }

          /* ==================================================================
           * DATE CHANGE
           * ================================================================== */

          else if (mounted) {

            setDateLoading(
              false,
            );
          }
        }
      };


    /* ========================================================================
     * EXECUTE
     * ======================================================================== */

    loadDashboard();


    /* ========================================================================
     * CLEANUP
     * ======================================================================== */

    return () => {

      mounted = false;


      if (isInitialLoad) {

        hideLoader();

      } else {

        setDateLoading(
          false,
        );
      }
    };

  }, [
    ManagerEmployeeCode,
    fromDateString,
    toDateString,
    showLoader,
    hideLoader,
  ]);


  /* ==========================================================================
   * PAGE
   * ========================================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        space-y-4
      "
    >

      {/* ======================================================================
          DASHBOARD HEADER
          ====================================================================== */}

      <DashboardHeader
        breadcrumbs={[
          {
            label:
              'Work Vista',
            path:
              '/',
          },
          {
            label:
              'Reporting Manager Dashboard',
          },
        ]}
      />


      {/* ======================================================================
          MAIN DASHBOARD CARD
          ====================================================================== */}

      <div
        className="
          relative
          box-border
          w-full
          min-w-0
          overflow-hidden
          rounded-[16px]
          border
          border-[#E2E8F0]
          bg-white
        "
      >

        {/* ====================================================================
            SPAN OF CONTROL HEADER
            ==================================================================== */}

        <div
          className="
            relative
            border-l-[3px]
            border-l-[#0B3A75]
            bg-white
            px-5
            py-4
            sm:px-6
            sm:py-[18px]
          "
        >

          <div
            className="
              flex
              min-w-0
              flex-col
              gap-3
              lg:flex-row
              lg:items-center
              lg:justify-between
            "
          >

            {/* ================================================================
                TITLE
                ================================================================ */}

            <div
              className="
                min-w-0
                flex-1
              "
            >

              <div
                className="
                  flex
                  min-w-0
                  flex-wrap
                  items-center
                  gap-2
                "
              >

                <h1
                  className="
                    whitespace-nowrap
                    text-[16px]
                    font-semibold
                    leading-[22px]
                    text-[#002B5B]
                    sm:text-[17px]
                  "
                >
                  Span of Control ·{' '}
                  {
                    headerData
                      ?.totalTeamLeads ??
                    0
                  }{' '}
                  Teams
                </h1>


                {/* ==========================================================
                    MANAGER NAME
                    ========================================================== */}

                <div
                  className="
                    max-w-full
                    truncate
                    rounded-full
                    bg-[#EEF6FF]
                    px-3
                    py-[5px]
                    text-[11px]
                    font-medium
                    leading-[16px]
                    text-[#1769C2]
                    sm:text-[12px]
                  "
                  title={
                    `Reporting Manager: ${
                      headerData
                        ?.operationalManagerName ??
                      '-'
                    }`
                  }
                >
                  Reporting Manager:{' '}
                  {
                    headerData
                      ?.operationalManagerName ??
                    '-'
                  }
                </div>

              </div>


              {/* ==============================================================
                  SELECTED DATE
                  ============================================================== */}

              <p
                className="
                  mt-2
                  truncate
                  text-[12px]
                  leading-[18px]
                  text-[#64748B]
                  sm:text-[13px]
                "
              >

                {
                  fromDate.toLocaleDateString(
                    'en-GB',
                    {
                      weekday:
                        'short',

                      day:
                        '2-digit',

                      month:
                        'short',
                    },
                  )
                }

                {' · '}

                WTD vs Prior Week

              </p>

            </div>


            {/* =================================================================
                WORK MODE
                ================================================================= */}

            <div
              className="
                flex
                w-full
                min-w-0
                flex-wrap
                items-center
                gap-1
                lg:w-auto
                lg:flex-nowrap
              "
            >

              {[
                'All',
                'Work From Home',
                'Work From Office',
              ].map(
                (
                  mode,
                ) => (

                  <button
                    key={mode}
                    type="button"
                    onClick={() =>
                      setWorkMode(
                        mode,
                      )
                    }
                    className={`
                      whitespace-nowrap
                      rounded-lg
                      px-3
                      py-[7px]
                      text-[11px]
                      font-medium
                      transition-all
                      duration-200
                      sm:px-4
                      sm:text-xs

                      ${
                        workMode ===
                        mode
                          ? `
                            bg-[#0B3A75]
                            text-white
                            shadow-sm
                          `
                          : `
                            text-[#5F5F5F]
                            hover:bg-[#F1F5F9]
                          `
                      }
                    `}
                  >
                    {mode}
                  </button>

                ),
              )}

            </div>

          </div>

        </div>


        {/* ====================================================================
            DATA CONTENT
            ==================================================================== */}

        <div
          className={`
            relative
            min-w-0
            px-3
            pb-5
            transition-all
            duration-200
            sm:px-4
            sm:pb-6
            lg:px-5

            ${
              dateLoading
                ? `
                  pointer-events-none
                  select-none
                  blur-[2px]
                `
                : ''
            }
          `}
        >

          {/* ==================================================================
              KPI SECTION
              ================================================================== */}

          <div
            className="
              min-w-0
            "
          >

            <KPISection
              ManagerEmployeeCode={
                ManagerEmployeeCode
              }
            />

          </div>


          {/* ==================================================================
              CHARTS
              ================================================================== */}

          <div
            className="
              mt-5
              min-w-0
              sm:mt-6
            "
          >

            <ChartsSection
              ManagerEmployeeCode={
                ManagerEmployeeCode
              }
            />

          </div>


          {/* ==================================================================
              APPLICATION LIST
              ================================================================== */}

          <div
            className="
              mt-5
              min-w-0
              sm:mt-6
            "
          >

            <AppList
              ManagerEmployeeCode={
                ManagerEmployeeCode
              }
            />

          </div>


          {/* ==================================================================
              HEATMAP
              ================================================================== */}

          <div
            className="
              mt-5
              min-w-0
              sm:mt-6
            "
          >

           <Heatmap
  managerEmployeeCode={
    ManagerEmployeeCode
  }
/>

          </div>

        </div>


        {/* ====================================================================
            DATE CHANGE LOADER
            ==================================================================== */}

        {dateLoading && (
          <DateChangeLoader />
        )}

      </div>

    </div>
  );
};


/* ============================================================================
 * PAGE
 * ========================================================================== */

const RMDashboard = () => {

  /* ==========================================================================
   * TOKEN CHECK
   * ========================================================================== */

  const token =
    localStorage.getItem(
      'token',
    );


  if (!token) {

    return (
      <Navigate
        to="/"
        replace
      />
    );
  }


  /* ==========================================================================
   * CURRENT ROLE
   * ========================================================================== */

  const currentRole =
    localStorage.getItem(
      'role',
    ) ||
    'Employee';


  /* ==========================================================================
   * ROLES
   * ========================================================================== */

  let roles: {
    roleId: number;
    roleName: string;
  }[] = [];


  try {

    roles =
      JSON.parse(
        localStorage.getItem(
          'roles',
        ) ||
        '[]',
      );

  } catch (
    error
  ) {

    console.error(
      'Unable to parse roles:',
      error,
    );

    roles = [];
  }


  /* ==========================================================================
   * MANAGER ROLE
   * ========================================================================== */

  const hasManagerRole =
    roles.some(
      (
        role,
      ) =>
        role.roleName ===
        'Manager',
    );


  if (
    currentRole !==
      'Manager' ||
    !hasManagerRole
  ) {

    return (
      <Navigate
        to="/"
        replace
      />
    );
  }


  /* ==========================================================================
   * MANAGER DATE PROVIDER
   *
   * IMPORTANT:
   *
   * Only ManagerDateProvider is required here.
   *
   * ManagerDateProvider supplies:
   *
   *   useManagerDate()
   *
   * and also exposes the same values to:
   *
   *   useDashboardDate()
   *
   * for DashboardHeader.
   * ========================================================================== */

  return (
    <ManagerDateProvider>
      <DashboardContent />
    </ManagerDateProvider>
  );
};


export default RMDashboard;